import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { environment } from '@env/environment';
import { ApiClient } from '@shared/api-client';
import {
  GetAllNotificationParams,
  GetAllNotificationResponse,
  MaskAsReadNotificationBody,
  SendAlertBody,
} from '@shared/apis/api.model';
import { firstValueFrom, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly api: ApiClient;

  constructor(
    private readonly swPush: SwPush,
    private readonly http: HttpClient,
  ) {
    this.api = new ApiClient(this.http, { responseTimeout: environment.API_TIMEOUT });
  }

  async requestPermission() {
    if (environment.MOCK_DATA) return;
    if (!('Notification' in window)) {
      console.error('This browser does not support notifications.');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      await this.registerDevice();
    } else {
      console.warn('Notification permission denied.');
    }
  }

  async registerDevice(): Promise<void> {
    if (!this.swPush.isEnabled) return;

    try {
      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: environment.VAPID_PUBLIC_KEY,
      });

      await firstValueFrom(this.api.noti.registerDevice(subscription));
    } catch (err) {
      console.error('Could not subscribe to notifications', err);
    }
  }

  async unregisterDevice(): Promise<void> {
    if (environment.MOCK_DATA) return;
    if (!this.swPush?.isEnabled || !('serviceWorker' in navigator)) {
      return;
    }

    const subscription = await firstValueFrom(this.swPush.subscription);
    if (!subscription) return;

    await firstValueFrom(this.api.noti.unregisterDevice(subscription));
    await subscription.unsubscribe();
  }

  sendAlert(body: SendAlertBody): Observable<any> {
    return this.api.noti.sendAlert(body);
  }

  getAllNotification(params: GetAllNotificationParams): Observable<GetAllNotificationResponse> {
    return this.api.noti.getAllNotification(params);
  }

  maskAsReadNotification(body: MaskAsReadNotificationBody): Observable<any> {
    return this.api.noti.maskAsReadNotification(body);
  }

  maskAllReadNotification(body: MaskAsReadNotificationBody): Observable<any> {
    return this.api.noti.maskAllReadNotification(body);
  }
}
